import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Shared pieces for the Orders Management sections that start from a past
// order (RestoreOrder, CopyOrder): searching every order for the school,
// deleted ones included, and picking which order date to put the result on.

export function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

export function orderDateLabel(d) {
  return d.title || formatDate(d.delivery_date);
}

// Loads the school's order dates and tracks a selected one, defaulting to
// the soonest delivery date that hasn't passed yet (else the latest).
export function useOrderDates(apiBase) {
  const [orderDates, setOrderDates] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${apiBase}/order-dates`).then(res => {
      const dates = res.data || [];
      setOrderDates(dates);
      const now = new Date();
      const upcoming = dates.find(d => new Date(d.delivery_date) >= now);
      const fallback = upcoming || dates[dates.length - 1];
      if (fallback) setSelectedId(String(fallback.id));
    }).catch(() => setError('Failed to load order dates'));
  }, [apiBase]);

  const selected = orderDates.find(d => String(d.id) === selectedId) || null;
  return { orderDates, selectedId, setSelectedId, selected, error };
}

export function OrderDateSelect({ id, label, orderDates, value, onChange }) {
  return (
    <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap'}}>
      <label htmlFor={id} style={{fontSize: '0.9rem'}}>{label}</label>
      <select
        id={id}
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{padding: '0.4rem', border: '1px solid #ced4da', borderRadius: '4px'}}
      >
        {orderDates.length === 0 && <option value="">No order dates</option>}
        {orderDates.map(d => (
          <option key={d.id} value={d.id}>
            {(d.title ? `${d.title} — ` : '') + formatDate(d.delivery_date)}{!d.is_active ? ' (closed)' : ''}
          </option>
        ))}
      </select>
    </div>
  );
}

const cell = { padding: '0.4rem 0.75rem', borderBottom: '1px solid #dee2e6', textAlign: 'left' };

// Search box + results table. renderAction(order) supplies each row's
// button, so each section decides what happens to a picked order.
export function OrderSearch({ apiBase, renderAction }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);

  const search = async (e) => {
    e.preventDefault();
    const q = query.trim();
    if (q.length < 2) {
      setError('Enter at least 2 characters');
      return;
    }
    setSearching(true);
    setError(null);
    try {
      const res = await axios.get(`${apiBase}/orders/search`, { params: { q } });
      setResults(res.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  return (
    <>
      <form onSubmit={search} style={{display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem'}}>
        <input
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Reference # or name"
          style={{flex: '1 1 200px', padding: '0.5rem', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '1rem'}}
        />
        <button
          type="submit"
          disabled={searching}
          style={{
            padding: '0.5rem 1.25rem',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            fontSize: '1rem',
            cursor: searching ? 'not-allowed' : 'pointer'
          }}
        >
          {searching ? 'Searching...' : 'Search'}
        </button>
      </form>

      {error && <div style={{color: '#dc3545', marginBottom: '1rem'}}>{error}</div>}

      {results && results.length === 0 && (
        <div style={{color: '#666'}}>No orders found.</div>
      )}

      {results && results.length > 0 && (
        <div style={{overflowX: 'auto'}}>
          <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem'}}>
            <thead>
              <tr style={{backgroundColor: '#e9ecef'}}>
                <th style={cell}>Ref #</th>
                <th style={cell}>Name</th>
                <th style={cell}>Grade</th>
                <th style={cell}>Order Date</th>
                <th style={cell}>Sandwich</th>
                <th style={cell}>Status</th>
                <th style={cell}></th>
              </tr>
            </thead>
            <tbody>
              {results.map(o => (
                <tr key={o.id}>
                  <td style={{...cell, fontFamily: 'monospace'}}>{o.reference_number}</td>
                  <td style={cell}>{o.name}</td>
                  <td style={cell}>{o.grade}{o.teacher ? ` · ${o.teacher}` : ''}</td>
                  <td style={cell}>{o.order_date ? orderDateLabel(o.order_date) : '—'}</td>
                  <td style={cell}>{[o.bread, o.meat, o.cookie, o.side].filter(Boolean).join(', ')}</td>
                  <td style={cell}>
                    {o.deleted_at ? <span style={{color: '#888'}}>deleted</span> : 'active'}
                    {o.paid ? ' · paid' : ''}
                  </td>
                  <td style={cell}>{renderAction(o)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

export const sectionStyle = {
  backgroundColor: '#f8f9fa',
  padding: '1.5rem',
  borderRadius: '8px',
  marginBottom: '1rem',
  border: '1px solid #dee2e6'
};

export function actionButtonStyle(disabled) {
  return {
    padding: '0.3rem 0.75rem',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    whiteSpace: 'nowrap'
  };
}
